import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import {
  LinkProps,
  NavigationCombinedData,
  NavigationProps,
  StyledNavigationProps,
} from './types/Navigation.types';
import {
  EgyptianNavigationWrapper,
  NavigationWrapper,
} from './styles/Navigation.styles';
import { useMedia } from '../../hooks/useMedia';
import MobileNavigation from './MobileNavigation';
import DesktopNavigation from './DesktopNavigation';

const StyledNavigation = ({
  children,
  isEgyptianSite,
  selectedIndex,
  hasTopLevelLinks: hasTopLevelLinks,
}: StyledNavigationProps): JSX.Element => {
  if (isEgyptianSite) {
    return (
      <NavigationWrapper top="-27px">
        <EgyptianNavigationWrapper selectedIndex={selectedIndex}>
          {children as JSX.Element}
        </EgyptianNavigationWrapper>
      </NavigationWrapper>
    );
  }

  if(hasTopLevelLinks) {
    return (
      <NavigationWrapper top="-34px">{children as JSX.Element}</NavigationWrapper>
    );

  }

  return <NavigationWrapper top="0px">{children as JSX.Element}</NavigationWrapper>;
};

export const PureNavigation: React.FC<LinkProps> = ({
  mainLinks,
  topLevelLinks,
  languageLink,
  brandLink,
  isEgyptianSite,
  secondLanguageUrlPrefix,
  isSecondLanguageOnUrl,
  signInLabel,
  signInUrl,
}) => {
  const [pageTitle, setPageTitle] = useState<string>('');
  const isDesktop = useMedia('(min-width: 1025px)', true);

  useEffect(() => {
    setTimeout(() => {
      // TODO: This two lines below is needed because the title of the html on the "/" path is not set. When this is fixed, then they can be removed.
      let metaName = document
        .querySelector('meta[property="og:title"]')
        ?.getAttribute('content');
      metaName = metaName ? metaName : 'Not set';
      setPageTitle(document.title.length == 0 ? metaName : document.title);
    }, 500);
  }, []);

  return (
    <StyledNavigation
      isEgyptianSite={isEgyptianSite}
      secondLanguageUrlPrefix={secondLanguageUrlPrefix}
      selectedIndex={topLevelLinks.findIndex(topLink => topLink.isSelected) + 1}
      mainLinks={mainLinks}
      isSecondLanguageOnUrl={isSecondLanguageOnUrl}
      signInLabel={signInLabel}
      signInUrl={signInUrl}
      hasTopLevelLinks={topLevelLinks.length > 0}
    >
      {isDesktop ? (
        <DesktopNavigation
          topLevelLinks={topLevelLinks}
          languageLink={languageLink}
          pageTitle={pageTitle}
          isSecondLanguageOnUrl={isSecondLanguageOnUrl}
          secondLanguageUrlPrefix={secondLanguageUrlPrefix}
          brandLink={brandLink}
          mainLinks={mainLinks}
          signInLabel={signInLabel}
          signInUrl={signInUrl}
        />
      ) : (
        <MobileNavigation
          brandLink={brandLink}
          mainLinks={mainLinks}
          topLevelLinks={topLevelLinks}
          languageLink={languageLink}
          pageTitle={pageTitle}
          secondLanguageUrlPrefix={secondLanguageUrlPrefix}
          isSecondLanguageOnUrl={isSecondLanguageOnUrl}
          signInLabel={signInLabel}
          signInUrl={signInUrl}
        />
      )}
    </StyledNavigation>
  );
};

const Navigation = ({
  firstLanguageLocalCode,
  secondLanguageUrlPrefix,
  isSecondLanguageOnUrl,
}: NavigationProps) => {
  const { allContentfulNavigation, allContentfulPageBuilder } =
    useStaticQuery<NavigationCombinedData>(graphql`
      {
        allContentfulNavigation {
          nodes {
            node_locale
            brandLink
            signInLabel
            signInUrl
            mainLinks {
              url
              name
              contentful_id
            }
            topLevelLinks {
              url
              name
              contentful_id
              isSelected
            }
            languageLink {
              name
              url
              contentful_id
            }
            isEgyptianSite
          }
        }

        allContentfulPageBuilder {
          nodes {
            slug
          }
        }
      }
    `);

  const firstLanguageNavData = allContentfulNavigation.nodes.find(
    node => node.node_locale.slice(0, 2) === firstLanguageLocalCode
  );
  const secondLanguageNavData = allContentfulNavigation.nodes.find(
    node => node.node_locale.slice(0, 2) === secondLanguageUrlPrefix
  );

  let usedLanguageNavData;
  if (isSecondLanguageOnUrl && secondLanguageNavData) {
    usedLanguageNavData = secondLanguageNavData;
  } else {
    usedLanguageNavData = firstLanguageNavData as LinkProps;
  }

  const {
    node_locale,
    brandLink,
    mainLinks,
    topLevelLinks,
    languageLink,
    signInLabel,
    signInUrl,
    isEgyptianSite,
  } = usedLanguageNavData;

  return (
    <PureNavigation
      node_locale={node_locale}
      mainLinks={mainLinks}
      topLevelLinks={topLevelLinks}
      languageLink={languageLink}
      brandLink={brandLink}
      signInLabel={signInLabel}
      signInUrl={signInUrl}
      isEgyptianSite={isEgyptianSite}
      pageBuilderSlugList={[
        ...new Set(
          allContentfulPageBuilder.nodes
            .filter(
              node =>
                !node.slug.includes('/') &&
                node.slug !== secondLanguageUrlPrefix
            )
            .map(node => node.slug)
        ),
      ]}
      secondLanguageUrlPrefix={secondLanguageUrlPrefix}
      isSecondLanguageOnUrl={isSecondLanguageOnUrl}
    ></PureNavigation>
  );
};

export default Navigation;
